'use client';

import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import classNames from 'clsx';
import styles from './TextOnPath.scss';
import * as React from 'react';
import semanticClassNames from '../TextOnPath.semanticClassNames';
import type { TextOnPathProps } from '../TextOnPath.types';
import { useSvgMeasurement } from './useSvgMeasurement';
import { TextOnPathA11yWrapper } from './components/TextOnPathA11yWrapper';
import { TextOnPathSvg } from './components/TextOnPathSvg';
import { ViewerTestIds } from '../constants';

/**
 * Parses a viewBox string into its numeric components
 * @param viewBox - The viewBox string (e.g., "0 0 100 100")
 * @returns An object with the parsed values or undefined if invalid
 */
function parseViewBox(viewBox: string | undefined) {
  if (!viewBox) {
    return undefined;
  }

  const [minX, minY, width, height] = viewBox.split(' ').map(Number);

  if ([minX, minY, width, height].some(isNaN)) {
    return undefined;
  }

  return { minX, minY, width, height };
}

/**
 * Calculates an adjusted SVG viewBox string by adding padding based on stroke width.
 * @param baseViewBox - The original viewBox string (e.g., "0 0 100 100") or undefined.
 * @param pathStrokeWidth - The stroke width in em units.
 * @param fontSizePx - The calculated font size in pixels.
 * @returns The adjusted viewBox string or the original baseViewBox if adjustment isn't needed or possible.
 */
export function calculateAdjustedViewBox(
  parsedViewBox:
    | { minX: number; minY: number; width: number; height: number }
    | undefined,
  pathStrokeWidth: number = 1.2,
  fontSizePx: number = 16,
): string | undefined {
  if (!parsedViewBox) {
    return undefined;
  }

  const { minX, minY, width, height } = parsedViewBox;
  const MIN_PADDING_EFFECT_FACTOR = 1.0;
  const paddingFactor = Math.max(MIN_PADDING_EFFECT_FACTOR, pathStrokeWidth);
  const strokeWidthForPaddingPx = paddingFactor * fontSizePx;
  const padding = strokeWidthForPaddingPx / 2;

  const adjMinX = minX - padding;
  const adjMinY = minY - padding;
  const adjWidth = width + 2 * padding;
  const adjHeight = height + 2 * padding;

  if (adjWidth >= 0 && adjHeight >= 0) {
    return `${adjMinX} ${adjMinY} ${adjWidth} ${adjHeight}`;
  }

  return `${parsedViewBox.minX} ${parsedViewBox.minY} ${parsedViewBox.width} ${parsedViewBox.height}`;
}

/**
 * Calculates the actual font size as a percentage based on the SVG viewBox dimensions.
 * @param fontSizePercent - The desired font size as a percentage
 * @param viewBox - The parsed viewBox object containing dimensions
 * @returns The calculated font size as a percentage value or undefined if inputs are invalid
 */
function getActualFontSize(
  fontSizePercent: number | undefined,
  viewBox:
    | {
        minX: number;
        minY: number;
        width: number;
        height: number;
      }
    | undefined,
): number | undefined {
  if (!fontSizePercent || !viewBox) {
    return undefined;
  }

  // Use average of width and height to provide balanced font sizing
  // that works well for both wide and tall viewBoxes
  const currentSize = (viewBox.width + viewBox.height) / 2;
  return (fontSizePercent / 100) * (currentSize / 3);
}

function TextOnPathRoot({
  translations,
  viewBox: viewBoxProp,
  pathLength,
  pathStrokeWidth,
  fontSize,
  customClassNames = [],
  animate,
  ...props
}: TextOnPathProps) {
  const parsedViewBox = parseViewBox(viewBoxProp);
  const actualFontSize = getActualFontSize(fontSize, parsedViewBox);
  const viewBox = calculateAdjustedViewBox(
    parsedViewBox,
    pathStrokeWidth,
    actualFontSize,
  );

  const hasPathLengthAndViewBoxOnProps = !!pathLength && !!viewBox;
  const { measureRefCallback, measurement, pathMeasurement } =
    useSvgMeasurement(hasPathLengthAndViewBoxOnProps);
  const hasPathAndViewBox =
    hasPathLengthAndViewBoxOnProps ||
    (!!pathMeasurement?.pathLength && !!pathMeasurement?.viewBox);
  const isAnimated = animate !== false && props.speed > 0 && !!measurement;
  const hasLink = !!props.link;
  const pauseOnHover = props.pauseOnHover || hasLink;

  return (
    <div
      id={props.id}
      data-testid={ViewerTestIds.root}
      className={classNames(
        styles.textOnPathRoot,
        formatClassNames(semanticClassNames.root),
        customClassNames,
      )}
      style={
        {
          '--path-stroke-width': `${pathStrokeWidth}em`,
          '--font-size': actualFontSize ? `${actualFontSize}px` : undefined,
        } as React.CSSProperties
      }
      {...getDataAttributes(props)}
      data-mode={props.fillMode}
      data-animated={animate !== false && props.speed > 0 ? '' : undefined}
    >
      {/* Measurement SVG */}
      <svg
        ref={measureRefCallback}
        aria-hidden="true"
        data-nosnippet="true"
        className={classNames(
          styles.textOnPathRootSvg,
          styles.textOnPathSvgMeasure,
        )}
        preserveAspectRatio="xMidYMid meet"
        viewBox={viewBox}
      >
        <path d={props.path} data-measure-path />
        <text data-measure-height x="0" y="0" dominantBaseline="hanging">
          <tspan>ÁjgqÎ|QWERTYqwerty</tspan>
        </text>

        <text x="0" y="0" data-measure-width>
          {props.text}
          {props.fillMode === 'repeat' ? <>&nbsp;</> : null}
        </text>
      </svg>
      {hasPathAndViewBox ? (
        <TextOnPathA11yWrapper
          translations={translations}
          pauseOnHover={pauseOnHover}
          isAnimated={isAnimated}
        >
          <TextOnPathSvg
            {...props}
            speed={isAnimated ? props.speed : 0}
            viewBox={viewBox || pathMeasurement?.viewBox}
            pathLength={pathLength || pathMeasurement?.pathLength}
            measurement={measurement}
            pauseOnHover={pauseOnHover}
            isAnimated={isAnimated}
          />
        </TextOnPathA11yWrapper>
      ) : null}
    </div>
  );
}

export default TextOnPathRoot;
